import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message envoyé avec succès !');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Téléphone',
      value: '07 69 11 91 83',
      href: 'tel:0769119183',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'burhan.arslan@laposte.net',
      href: 'mailto:burhan.arslan@laposte.net',
    },
    {
      icon: MapPin,
      label: 'Zone d\'intervention',
      value: 'Auvergne',
      href: null,
    },
    {
      icon: Clock,
      label: 'Horaires',
      value: 'Lun - Sam : 8h - 18h',
      href: null,
    },
  ];

  return (
    <div className="pt-16 md:pt-20 min-h-screen" data-testid="contact-page">
      {/* Header */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-white mb-6">
              Contactez-nous
            </h1>
            <p className="text-slate-400 text-lg md:text-xl leading-relaxed">
              Une question, un devis ? N'hésitez pas à nous contacter par téléphone ou via le formulaire ci-dessous.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-8">
                Nos coordonnées
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                {contactInfo.map((info) => (
                  <Card key={info.label} className="border-slate-100">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                          <info.icon className="w-6 h-6 text-sky-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-500 text-sm mb-1">{info.label}</p>
                          {info.href ? (
                            <a 
                              href={info.href} 
                              className="font-medium text-slate-900 hover:text-sky-600 transition-colors break-all text-sm sm:text-base"
                              data-testid={`contact-${info.label.toLowerCase()}`}
                            >
                              {info.value}
                            </a>
                          ) : (
                            <p className="font-medium text-slate-900 text-sm sm:text-base">{info.value}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Google Maps */}
              <div className="rounded-2xl overflow-hidden h-64 shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11284.843854856925!2d3.7352!3d45.5492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47f6f8e4c6c7e6f5%3A0x40819a5fd979a20!2s63600%20Ambert!5e0!3m2!1sfr!2sfr!4v1704067200000!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="BA NETTOYAGE - Ambert"
                  data-testid="google-map"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="border-none shadow-xl">
                <CardContent className="p-8">
                  <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-2">
                    Envoyez-nous un message
                  </h2>
                  <p className="text-slate-500 mb-8">
                    Remplissez le formulaire et nous vous répondrons dans les plus brefs délais.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-medium">Nom complet</Label>
                      <Input
                        id="name"
                        required
                        placeholder="Votre nom"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                        data-testid="contact-name"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          placeholder="votre@email.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                          data-testid="contact-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-medium">Téléphone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          required
                          placeholder="06 00 00 00 00"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-2 h-12 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                          data-testid="contact-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-700 font-medium">Message</Label>
                      <Textarea
                        id="message"
                        required
                        placeholder="Décrivez votre demande..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="mt-2 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                        rows={5}
                        data-testid="contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full rounded-full bg-slate-900 hover:bg-slate-800 h-12 text-base"
                      data-testid="contact-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
